/**
 * Identifiant de connexion d'un employé : initiale du prénom, point, nom de famille complet, `@groupereca.com`.
 * Francis Bastien → `f.bastien@groupereca.com`.
 *
 * Miroir exact de `normalize_login_part_v1` / `employee_login_base_v1` (migration 20261021010000). Le serveur
 * reste l'autorité : il recalcule l'identifiant et refuse toute valeur hors règle. Ce calcul local ne sert qu'à
 * l'aperçu immédiat pendant la saisie. L'identifiant ne crée aucune boîte courriel.
 */

export const employeeLoginDomain = 'groupereca.com'

const accented = 'àáâãäåāăąçćĉċčďđèéêëēĕėęěĝğġģĥħìíîïĩīĭįıĵķĺļľŀłñńņňòóôõöøōŏőŕŗřśŝşšșţťŧțùúûüũūŭůűųŵýÿŷźżž'
const plain = 'aaaaaaaaacccccddeeeeeeeeegggghhiiiiiiiiijklllllnnnnooooooooorrrsssssttttuuuuuuuuuuwyyyzzz'
const transliteration = new Map(
  Array.from(accented).map((character, index) => [character, plain.charAt(index)]),
)

/**
 * Règle des noms : minuscules, accents et ligatures translittérés (œ → oe), apostrophes retirées
 * (D'Amours → damours), espaces, points, soulignés et traits d'union regroupés en un seul trait d'union
 * (Saint-Pierre Tremblay → saint-pierre-tremblay). Le nom n'est jamais tronqué au premier mot.
 */
export function normalizeLoginPart(value: string): string {
  const lowered = value
    .normalize('NFC')
    .toLowerCase()
    .replaceAll('œ', 'oe')
    .replaceAll('æ', 'ae')
    .replaceAll('ß', 'ss')
  const transliterated = Array.from(lowered)
    .map((character) => transliteration.get(character) ?? character)
    .join('')
  return transliterated
    .replace(/['’‘`´ʼ]/g, '')
    .replace(/[\s_.‐–—-]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** `f.bastien`, ou `null` si le prénom ou le nom ne donne aucun caractère utilisable. */
export function employeeLoginBase(prenom: string, nom: string): string | null {
  const initial = /[a-z]/.exec(normalizeLoginPart(prenom))?.[0]
  const lastName = normalizeLoginPart(nom)
  if (!initial || !lastName) return null
  return `${initial}.${lastName}`
}

export function employeeIdentifier(prenom: string, nom: string, suffix?: number): string | null {
  const base = employeeLoginBase(prenom, nom)
  if (!base) return null
  return `${base}${suffix && suffix >= 2 ? String(suffix) : ''}@${employeeLoginDomain}`
}

/** Prénom ou nom tel qu'affiché : graphie et accents conservés, espaces superflus retirés. */
export function cleanPersonName(value: string): string {
  return value.trim().replace(/\s+/g, ' ')
}

export type IdentityIssue = 'PRENOM_REQUIRED' | 'NOM_REQUIRED' | 'NAMES_IDENTICAL' | 'UNUSABLE' | 'TOO_LONG'

export function identityIssue(prenom: string, nom: string): IdentityIssue | null {
  const cleanPrenom = cleanPersonName(prenom)
  const cleanNom = cleanPersonName(nom)
  if (!cleanPrenom) return 'PRENOM_REQUIRED'
  if (!cleanNom) return 'NOM_REQUIRED'
  if (cleanPrenom.length > 80 || cleanNom.length > 80) return 'TOO_LONG'
  if (cleanPrenom.toLowerCase() === cleanNom.toLowerCase()) return 'NAMES_IDENTICAL'
  if (!employeeLoginBase(cleanPrenom, cleanNom)) return 'UNUSABLE'
  return null
}

export const identityIssueLabels: Record<IdentityIssue, string> = {
  PRENOM_REQUIRED: 'Le prénom est obligatoire.',
  NOM_REQUIRED: 'Le nom est obligatoire.',
  NAMES_IDENTICAL: 'Le nom doit être distinct du prénom.',
  UNUSABLE: 'Ce prénom ou ce nom ne permet pas de former un identifiant.',
  TOO_LONG: 'Le prénom et le nom sont limités à 80 caractères.',
}
